import { useContext, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getDestinationDeals, type DestinationDealsCategory } from 'api/recommendations'
import GeoContext from 'contexts/geoContext'
import { useAppSelector } from 'hooks/reduxHooks'
import useOffers from 'hooks/Offers/useOffers'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { hasDisplayableOfferPrice } from 'lib/offer/pricingVisibility'
import { isSeasonalCarouselMarket, selectSeasonalCarouselOfferIds } from './seasonalCarouselConfig'
import { useHomepageHeroOfferIds, useHomepageHeroOfferIdsPublished } from './dedup/HomepageOfferDedupProvider'

const EMPTY_OFFER_IDS: Array<string> = []

export default function useSeasonalCarouselList(category: DestinationDealsCategory, overrideOfferList?: App.OfferList) {
  const { currentRegionCode } = useContext(GeoContext)
  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const heroOfferIds = useHomepageHeroOfferIds()
  const heroPublished = useHomepageHeroOfferIdsPublished()
  const waitingForHero = !overrideOfferList && !heroPublished
  const offerErrors = useAppSelector(state => state.offer.offerErrors)
  const loadedOffers = useAppSelector(state => state.offer.offers)
  const { data, isLoading, error } = useQuery({
    queryKey: ['destination-deals', currentRegionCode, category, isLuxPlusMember],
    queryFn: () => getDestinationDeals(currentRegionCode, category, isLuxPlusMember),
    enabled: isSeasonalCarouselMarket(currentRegionCode) && !overrideOfferList,
    staleTime: 10 * 60 * 1000,
  })
  const candidateIds = useMemo(() => overrideOfferList?.offerIds ?? data?.result.map(offer => offer.offer_id) ?? EMPTY_OFFER_IDS,
    [data, overrideOfferList])
  const unavailableOfferIds = useMemo(() => new Set(candidateIds.filter(id => {
    const offer = loadedOffers[id]
    return offer ? !hasDisplayableOfferPrice(offer) : !!offerErrors[id]
  })), [candidateIds, loadedOffers, offerErrors])
  // As hydration settles, replace failed/unpriced selections with the next ranked candidates.
  const selectedIds = useMemo(() => selectSeasonalCarouselOfferIds(
    candidateIds, waitingForHero ? undefined : heroOfferIds, overrideOfferList ? undefined : unavailableOfferIds,
  ), [candidateIds, heroOfferIds, waitingForHero, overrideOfferList, unavailableOfferIds])
  const [offers, fetchingOffers] = useOffers(overrideOfferList ? EMPTY_OFFER_IDS : selectedIds)

  const offerList = useMemo((): App.OfferList => {
    if (overrideOfferList) {
      return { ...overrideOfferList, offerIds: selectedIds }
    }

    const waitingForOffers = selectedIds.some(id => !offers.some(offer => offer.id === id) && !offerErrors[id])
    return {
      key: `destination-deals:${currentRegionCode}:${category}:${isLuxPlusMember}`,
      offerIds: offers.filter(hasDisplayableOfferPrice).map(offer => offer.id),
      fetching: waitingForHero || isLoading || fetchingOffers || waitingForOffers,
      error,
    }
  }, [overrideOfferList, selectedIds, offers, offerErrors, currentRegionCode, category, isLuxPlusMember, waitingForHero, isLoading, fetchingOffers, error])

  return { offerList }
}
